import React, { useMemo, useState, useRef, useEffect } from 'react';
import { format } from 'date-fns';
import {
  X, Play, MoreHorizontal, Lightbulb, Sparkles,
  Loader2, Wand2, Trash2, Edit3, TrendingUp, TrendingDown,
  Eye, EyeOff, RefreshCw, Pencil
} from 'lucide-react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import { Textarea } from './ui/textarea';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator
} from './ui/dropdown-menu';

// ------------------------------------------------------------------
// Helpers (pure) — colocated so this file is fully self-contained.
// ------------------------------------------------------------------
const BRAND_PALETTE = [
  { bg: '#7c3aed', ring: '#ede9fe' },
  { bg: '#0ea5e9', ring: '#e0f2fe' },
  { bg: '#0f766e', ring: '#ccfbf1' },
  { bg: '#ea580c', ring: '#ffedd5' },
  { bg: '#db2777', ring: '#fce7f3' },
  { bg: '#4f46e5', ring: '#e0e7ff' },
];
const brandColor = (name) => {
  const s = String(name || 'X').toUpperCase();
  const hash = [...s].reduce((h, c) => h + c.charCodeAt(0), 0);
  return BRAND_PALETTE[hash % BRAND_PALETTE.length];
};

const fmtClock = (sec) => {
  if (sec == null || Number.isNaN(sec)) return '0:00';
  const total = Math.max(0, Math.round(sec));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
};

const CTA_WORDS = ['shop', 'buy', 'order', 'get', 'try', 'click', 'sign up', 'download', 'grab', 'book', 'call', 'learn more', 'add to cart', 'karo', 'karein', 'lo', 'lein'];
const isCTA = (t) => {
  const s = String(t || '').toLowerCase();
  return CTA_WORDS.some((w) => s.includes(w));
};

// Tag each transcript segment as Hook / Claim / CTA / (none).
const tagSegments = (segments) => {
  if (!Array.isArray(segments) || segments.length === 0) return [];
  const last = segments.length - 1;
  return segments.map((s, i) => {
    if (i === 0) return { ...s, tag: 'hook' };
    if (isCTA(s.text)) return { ...s, tag: 'cta' };
    if (i === last) return { ...s, tag: isCTA(s.text) ? 'cta' : null };
    return { ...s, tag: 'claim' };
  });
};

// Small reusable UI atoms — hoisted so they don't remount every render.
const TagPill = ({ tag }) => {
  if (!tag) return null;
  const map = {
    hook: 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100',
    claim: 'bg-amber-50 text-amber-700 ring-1 ring-amber-100',
    cta: 'bg-violet-50 text-violet-700 ring-1 ring-violet-100',
  };
  const labels = { hook: 'Hook', claim: 'Claim', cta: 'CTA' };
  return (
    <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold tracking-wide ${map[tag]}`}>
      {labels[tag]}
    </span>
  );
};

const StatCard = ({ value, label, delta, deltaPositive, wide }) => (
  <div className={`bg-white rounded-2xl border border-slate-200 px-4 py-3 min-w-0 ${wide ? 'flex-[1.4]' : 'flex-1'}`}>
    <div className="flex items-start justify-between gap-2">
      <div className="min-w-0">
        <p className="text-[22px] leading-none font-bold text-slate-900 tabular-nums truncate">{value}</p>
        <p className="text-[11px] font-medium text-slate-500 mt-1.5">{label}</p>
      </div>
      {delta && (
        <span className={`text-[11px] font-semibold tabular-nums shrink-0 ${deltaPositive ? 'text-emerald-600' : 'text-rose-500'}`}>
          {delta}
        </span>
      )}
    </div>
  </div>
);

const MiniInsightCard = ({ label, quote, comparison, comparisonPositive }) => (
  <div className="bg-white rounded-2xl border border-slate-200 px-4 py-3 min-w-0 flex flex-col">
    <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-[0.12em]">{label}</p>
    <p className="text-sm font-medium text-slate-800 mt-1.5 whitespace-normal break-words">
      {quote || <span className="text-slate-400 italic font-normal">—</span>}
    </p>
    {comparison && (
      <p className={`text-[11px] font-semibold mt-2 ${comparisonPositive ? 'text-emerald-600' : 'text-slate-500'}`}>
        {comparison}
      </p>
    )}
  </div>
);

// ------------------------------------------------------------------
// Main component
// ------------------------------------------------------------------
export function CreativeStructuredDetail({
  creative,
  apiUrl,
  onClose,
  onEditMetrics,
  onDelete,
  onSaveAdName,
  onSaveLearning,
  onRephraseLearning,
  rephrasingLearning,
  onReanalyze,
  reanalyzing,
  onGenerateLearningAnalysis,
  learningAnalysisLoading,
}) {
  // Video playhead state — used to highlight the current transcript row and
  // paint the mini timeline scrubber below the media preview.
  const videoRef = useRef(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);

  // Videos are lazily fetched via /api/creative-analyses/{id}/video so we
  // don't ship the whole base64 blob in the list response.
  const [videoData, setVideoData] = useState(null);
  const [loadingVideo, setLoadingVideo] = useState(false);
  useEffect(() => { setVideoData(null); }, [creative?.id]);

  useEffect(() => { setCurrentTime(0); setIsPlaying(false); }, [creative?.id]);

  // Language toggle for the transcript panel — "Original" vs "English".
  // Defaults to English when a translation exists (regional-language ads,
  // the reason the user asked for this feature); falls back to 'original'
  // otherwise so pure-English or Hinglish transcripts still render.
  const [transcriptLang, setTranscriptLang] = useState('original');
  useEffect(() => {
    const hasEn = !!(creative?.audio_info?.analysis?.transcript_english || '').trim();
    setTranscriptLang(hasEn ? 'english' : 'original');
  }, [creative?.id, creative?.audio_info?.analysis?.transcript_english]);

  // Video-caption visibility — persisted across sessions.
  const [captionsOn, setCaptionsOn] = useState(() => {
    try { return localStorage.getItem('prism_creative_captions_on') !== '0'; }
    catch (_) { return true; }
  });
  useEffect(() => {
    try { localStorage.setItem('prism_creative_captions_on', captionsOn ? '1' : '0'); } catch (_) {}
  }, [captionsOn]);

  const brand = creative?.brand || creative?.metrics?.brand || 'X';
  const bc = brandColor(brand);
  const initial = String(brand).trim().charAt(0).toUpperCase() || 'X';

  // Title: "{brand} · {ad_name}" — used only for a11y title.
  const adName = (creative?.metrics?.ad_name || '').trim();

  const analysis = creative?.analysis || {};
  const audio = creative?.audio_info || {};
  const audioAnalysis = audio?.analysis || {};
  const metrics = creative?.metrics || {};

  // ===== Learning-based analysis view =====
  // A parallel copy of the structured analysis rewritten through the lens of
  // the marketer's learning. Lives on the doc as `analysis_from_learning`;
  // the parent regenerates it on-demand and pushes it back onto the creative.
  // Transcript is intentionally NOT swapped — only the interpretive cards.
  const hasLearning = !!(creative?.learning && String(creative.learning).trim().length >= 5);
  const learningAnalysis = creative?.analysis_from_learning || null;
  const [viewMode, setViewMode] = useState('ai'); // 'ai' | 'learning'
  useEffect(() => { setViewMode('ai'); }, [creative?.id]);

  const showLearningView = viewMode === 'learning' && !!learningAnalysis;
  const effectiveAnalysis = showLearningView ? learningAnalysis : analysis;
  const effectiveAudioAnalysis = showLearningView
    ? (learningAnalysis?.audio?.analysis || {})
    : audioAnalysis;
  const effectiveAudioLanguage = showLearningView
    ? (learningAnalysis?.audio?.language || audio?.language)
    : audio?.language;

  const handleSwitchToLearning = () => {
    if (!hasLearning) return;
    setViewMode('learning');
    if (!learningAnalysis && onGenerateLearningAnalysis) {
      onGenerateLearningAnalysis(false);
    }
  };
  const handleRefreshLearningAnalysis = () => {
    if (onGenerateLearningAnalysis) onGenerateLearningAnalysis(true);
  };

  // Timestamped segments (from Whisper). Fall back to a single un-timed row
  // built from the raw transcript so the layout still holds together on
  // pre-existing analyses that were recorded before we captured segments.
  const segments = useMemo(() => {
    const s = Array.isArray(audio?.segments) ? audio.segments : [];
    if (s.length > 0) return tagSegments(s);
    const t = (audio?.transcription || '').trim();
    if (!t) return [];
    return [{ start: 0, end: audio?.duration_seconds || 0, text: t, tag: 'hook' }];
  }, [audio?.segments, audio?.transcription, audio?.duration_seconds]);

  // Video source URL — first analyzed frame if it's a video, else the base64 image.
  const primaryFrameUrl = creative?.is_video && Array.isArray(creative?.analyzed_frames) && creative.analyzed_frames[0]
    ? `${apiUrl}${creative.analyzed_frames[0].url}`
    : null;
  const rawImageSrc = creative?.image_base64
    ? `data:${creative.content_type || 'image/png'};base64,${creative.image_base64}`
    : null;
  // Resolved playable video src — set once /video endpoint returns.
  const videoUrl = videoData
    ? (videoData.video_url
        ? `${apiUrl}${videoData.video_url}`
        : (videoData.video_base64
            ? `data:${videoData.content_type || 'video/mp4'};base64,${videoData.video_base64}`
            : null))
    : null;

  // Full English translation (for regional-language sources — Tamil, etc.)
  const englishTranscript = (audioAnalysis?.transcript_english || '').trim();
  const hasEnglishTranslation = englishTranscript.length > 0;
  const isRegional = hasEnglishTranslation;

  // Compute mini timeline segments (colored strip below media)
  const timelineSegments = useMemo(() => {
    const total = (audio?.duration_seconds || duration || 0) || segments.reduce((m, s) => Math.max(m, s.end || 0), 0);
    if (!total || segments.length === 0) return [];
    return segments.map((s) => ({
      tag: s.tag,
      left: `${((s.start || 0) / total) * 100}%`,
      width: `${(Math.max(0.1, (s.end || 0) - (s.start || 0)) / total) * 100}%`,
    }));
  }, [segments, audio?.duration_seconds, duration]);

  // Currently active segment (highlighted row in the transcript)
  const activeIdx = useMemo(() => {
    if (segments.length === 0) return -1;
    let i = segments.findIndex((s) => currentTime >= (s.start || 0) && currentTime < (s.end || 0));
    if (i === -1 && currentTime > 0) i = segments.length - 1;
    return i;
  }, [segments, currentTime]);

  // Per-segment English text, aligned 1:1 with `segments`.
  //  • Preferred source: `audioAnalysis.transcript_english_segments` (array
  //    of N strings). This is emitted by the backend prompt going forward.
  //  • Fallback: split the single `transcript_english` blob by sentence
  //    boundaries and distribute proportionally across the N segments so
  //    the on-screen caption still translates approximately in-sync.
  const englishBySegment = useMemo(() => {
    if (!hasEnglishTranslation || segments.length === 0) return [];
    const arr = audioAnalysis?.transcript_english_segments;
    if (Array.isArray(arr) && arr.length === segments.length) {
      return arr.map((s) => (typeof s === 'string' ? s : '')).map((s) => s.trim());
    }
    const sentences = englishTranscript
      .split(/(?<=[.!?])\s+/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (sentences.length === 0) return segments.map(() => '');
    // Distribute sentences across segments proportional to segment count.
    const per = sentences.length / segments.length;
    return segments.map((_, i) => {
      const lo = Math.floor(i * per);
      const hi = Math.max(lo + 1, Math.floor((i + 1) * per));
      return sentences.slice(lo, hi).join(' ');
    });
  }, [hasEnglishTranslation, segments, englishTranscript, audioAnalysis?.transcript_english_segments]);

  // Auto-advancing CAPTION for the video overlay. When we only have a
  // single big segment (older analyses that predate Whisper `segments`),
  // the full text gets clipped. Break the active segment's text into
  // sentence-sized chunks and cycle through them proportional to the
  // segment's elapsed time so the caption feels like a live subtitle
  // instead of a static wall of text.
  const captionChunk = useMemo(() => {
    if (activeIdx < 0) return { text: '', tag: null };
    const seg = segments[activeIdx];
    if (!seg) return { text: '', tag: null };
    const source = (transcriptLang === 'english' && hasEnglishTranslation)
      ? (englishBySegment[activeIdx] || seg.text)
      : seg.text;
    const chunks = String(source || '')
      .split(/(?<=[.!?])\s+/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (chunks.length === 0) return { text: '', tag: seg.tag };
    const segDur = Math.max(0.1, (seg.end || 0) - (seg.start || 0));
    const rel = Math.max(0, Math.min(1, (currentTime - (seg.start || 0)) / segDur));
    const idx = Math.min(chunks.length - 1, Math.floor(rel * chunks.length));
    return { text: chunks[idx], tag: seg.tag, chunkIdx: idx, chunkCount: chunks.length };
  }, [activeIdx, segments, currentTime, transcriptLang, hasEnglishTranslation, englishBySegment]);

  // Metric strip — show ROAS as a big multiplier + CTR/Hold/CVR/CPA as
  // secondary tiles. Deltas are shown only if computed on the row.
  const metricTiles = useMemo(() => {
    const tiles = [];
    if (metrics.roas != null) {
      tiles.push({ value: `${Number(metrics.roas).toFixed(1)}x`, label: 'ROAS', delta: null, wide: true });
    }
    if (metrics.ctr != null) tiles.push({ value: `${Number(metrics.ctr).toFixed(1)}%`, label: 'CTR' });
    if (metrics.hold_rate != null || metrics.hold != null) {
      const v = metrics.hold_rate ?? metrics.hold;
      tiles.push({ value: `${Number(v).toFixed(0)}%`, label: 'Hold' });
    }
    if (metrics.cvr != null) tiles.push({ value: `${Number(metrics.cvr).toFixed(1)}%`, label: 'CVR' });
    if (metrics.cpa != null) tiles.push({ value: `₹${Number(metrics.cpa).toLocaleString()}`, label: 'CPA' });
    if (metrics.spend != null && tiles.length < 5) tiles.push({ value: `₹${Number(metrics.spend).toLocaleString()}`, label: 'Spend' });
    return tiles;
  }, [metrics]);

  // Insight card (rightmost of the metrics row) — short, punchy.
  const insightHead = effectiveAudioAnalysis?.hook_strength
    ? (effectiveAudioAnalysis.hook_strength === 'strong'
        ? 'Strong hook.'
        : effectiveAudioAnalysis.hook_strength === 'weak' ? 'Weak hook.' : 'Moderate hook.')
    : (creative?.learning_rephrased ? 'Actionable summary ready.' : '');
  const insightBody = (creative?.learning_rephrased || creative?.learning || '').split(/\n+/)[0] || 'Add a learning to sharpen the takeaway.';

  // Category tags in the meta row.
  const categoryTags = [
    effectiveAnalysis?.theme,
    effectiveAnalysis?.emotion,
    effectiveAudioAnalysis?.target_audience,
    effectiveAnalysis?.ad_format,
  ].filter(Boolean);

  // Learning capture — controlled text so we can show a "Save" button whose
  // state accurately reflects unsaved edits. Also lifts the "LEARNING" pill
  // out of the header (where it was misplaced) into this card, where it
  // signals saved state right next to the source of truth.
  const [learningDraft, setLearningDraft] = useState(creative?.learning || '');
  useEffect(() => { setLearningDraft(creative?.learning || ''); }, [creative?.id, creative?.learning]);
  const learningDirty = (learningDraft || '').trim() !== (creative?.learning || '').trim();
  const learningSaved = !!(creative?.learning || '').trim() && !learningDirty;
  const saveLearningNow = () => {
    const v = (learningDraft || '').trim();
    if (v !== (creative?.learning || '').trim()) onSaveLearning(v);
  };

  // Autoplay / play toggle for video preview. First tap lazy-loads the video
  // (base64/GridFS via /api/creative-analyses/{id}/video), then toggles play/pause.
  const togglePlay = async () => {
    if (!creative?.is_video) return;
    if (!videoData) {
      setLoadingVideo(true);
      try {
        const res = await fetch(`${apiUrl}/api/creative-analyses/${creative.id}/video`, { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          setVideoData(data);
          // Play after the <video> element mounts with the resolved src.
          setTimeout(() => {
            const v = videoRef.current;
            if (v) { v.play().catch(() => {}); setIsPlaying(true); }
          }, 60);
        }
      } catch (_) { /* silent — button remains available */ }
      finally { setLoadingVideo(false); }
      return;
    }
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) { v.play().catch(() => {}); setIsPlaying(true); }
    else { v.pause(); setIsPlaying(false); }
  };
  const onLoadedMeta = (e) => setDuration(e.currentTarget.duration || 0);
  const onTimeUpdate = (e) => setCurrentTime(e.currentTarget.currentTime || 0);

  const seekTo = (sec) => {
    const v = videoRef.current;
    if (!v) { setCurrentTime(sec); return; }
    try { v.currentTime = Math.max(0, sec); setCurrentTime(sec); } catch (_) {}
  };

  if (!creative) return null;

  return (
    <Dialog open={!!creative} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent
        className="w-[96vw] max-w-[1400px] h-[94vh] p-0 bg-[#f7f7f5] overflow-hidden flex flex-col sm:rounded-2xl"
        data-testid="creative-structured-detail"
      >
        {/* Accessible title/description — visually hidden so the layout is
            unchanged but screen readers and Radix's a11y contract are happy. */}
        <DialogTitle className="sr-only">Creative analysis for {brand} — {adName || 'Untitled'}</DialogTitle>
        <DialogDescription className="sr-only">
          Structured performance summary, transcript with hook/claim/CTA tagging, and analysis breakdown.
        </DialogDescription>
        {/* ===================== HEADER ===================== */}
        <header className="shrink-0 flex items-center justify-between gap-4 pl-5 md:pl-6 pr-14 md:pr-16 py-4 bg-white border-b border-slate-200/70">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <span
              className="w-11 h-11 rounded-xl flex items-center justify-center text-white text-lg font-bold shrink-0"
              style={{ background: bc.bg, boxShadow: `0 0 0 4px ${bc.ring}` }}
              data-testid="header-brand-avatar"
            >
              {initial}
            </span>
            <div className="min-w-0">
              <label className="text-[10px] font-semibold text-slate-400 uppercase tracking-[0.14em] flex items-center gap-1" htmlFor="header-ad-name-input">
                <Pencil className="h-2.5 w-2.5" /> Ad name
              </label>
              <div className="flex items-baseline gap-2 min-w-0 group relative">
                <input
                  id="header-ad-name-input"
                  type="text"
                  defaultValue={adName}
                  placeholder={`${brand} · Untitled`}
                  onBlur={(e) => {
                    const v = e.target.value.trim();
                    if (v !== adName) onSaveAdName(v);
                  }}
                  onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                  title="Click to edit the ad name"
                  className="text-[15px] font-semibold text-slate-900 bg-transparent border-0 border-b border-dashed border-slate-300 hover:border-solid hover:border-violet-400 focus:border-solid focus:border-violet-500 focus:outline-none px-0 py-0.5 max-w-[420px] placeholder:text-slate-400 placeholder:font-normal truncate cursor-text transition-colors"
                  data-testid="header-ad-name-input"
                />
                <Pencil className="h-3 w-3 text-slate-300 group-hover:text-violet-500 group-focus-within:text-violet-500 transition-colors shrink-0" aria-hidden />
              </div>
              <p className="text-[11px] text-slate-400 mt-0.5">
                {creative?.created_at && format(new Date(creative.created_at), 'MMM d, yyyy')}
                {creative?.analyzed_by ? ` · ${creative.analyzed_by.split('@')[0]}` : ''}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {creative?.is_video && onReanalyze && (
              <button
                onClick={onReanalyze}
                disabled={reanalyzing}
                className="hidden sm:inline-flex items-center gap-1.5 px-3 h-9 rounded-full text-[13px] font-medium bg-violet-50 text-violet-700 hover:bg-violet-100 border border-violet-200 disabled:opacity-60 disabled:cursor-not-allowed transition"
                data-testid="header-reanalyze"
                title="Re-run AI analysis on this video (regenerates transcripts + English translation)"
              >
                {reanalyzing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                {reanalyzing ? 'Re-analyzing…' : 'Re-analyze'}
              </button>
            )}
            <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="w-9 h-9 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-500"
                aria-label="More actions"
                data-testid="header-more-menu"
              >
                <MoreHorizontal className="h-5 w-5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              <DropdownMenuItem onClick={onEditMetrics} data-testid="menu-edit-metrics">
                <Edit3 className="h-4 w-4 mr-2" /> Edit metrics
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onRephraseLearning} disabled={rephrasingLearning || !(creative?.learning || '').trim()} data-testid="menu-rephrase">
                {rephrasingLearning ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Wand2 className="h-4 w-4 mr-2" />}
                Rephrase learning
              </DropdownMenuItem>
              {creative?.is_video && onReanalyze && (
                <DropdownMenuItem onClick={onReanalyze} disabled={reanalyzing} data-testid="menu-reanalyze">
                  {reanalyzing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                  Re-analyze video
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={onDelete} className="text-rose-600 focus:text-rose-600" data-testid="menu-delete">
                <Trash2 className="h-4 w-4 mr-2" /> Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          </div>
        </header>

        {/* ============ ANALYSIS VIEW TOGGLE (AI vs Learning) ============ */}
        <div className="shrink-0 px-5 md:px-6 pt-3 pb-2 bg-white border-b border-slate-100 flex items-center gap-3">
          <div
            className="inline-flex items-center bg-white rounded-full p-1 shadow-[0_2px_8px_rgba(15,23,42,0.06),0_0_0_1px_rgba(15,23,42,0.06)] relative"
            role="tablist"
            aria-label="Analysis view"
          >
            <button
              type="button"
              role="tab"
              aria-selected={viewMode === 'ai'}
              onClick={() => setViewMode('ai')}
              className={`relative z-10 text-[13px] font-semibold px-6 py-2 rounded-full transition-colors duration-200 ${
                viewMode === 'ai'
                  ? 'text-white'
                  : 'text-slate-500 hover:text-slate-800'
              }`}
              style={
                viewMode === 'ai'
                  ? { background: 'linear-gradient(135deg,#7c3aed 0%,#a21caf 100%)', boxShadow: '0 4px 14px rgba(124,58,237,0.35)' }
                  : undefined
              }
              data-testid="analysis-view-ai"
            >
              AI analysis
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={viewMode === 'learning'}
              onClick={handleSwitchToLearning}
              disabled={!hasLearning}
              title={hasLearning ? 'Regenerate the analysis through your saved learning' : 'Save a learning first to unlock this view'}
              className={`relative z-10 inline-flex items-center gap-1.5 text-[13px] font-semibold px-6 py-2 rounded-full transition-colors duration-200 ${
                !hasLearning
                  ? 'text-slate-300 cursor-not-allowed'
                  : viewMode === 'learning'
                    ? 'text-white'
                    : 'text-slate-500 hover:text-slate-800'
              }`}
              style={
                viewMode === 'learning' && hasLearning
                  ? { background: 'linear-gradient(135deg,#7c3aed 0%,#a21caf 100%)', boxShadow: '0 4px 14px rgba(124,58,237,0.35)' }
                  : undefined
              }
              data-testid="analysis-view-learning"
            >
              <Lightbulb className="h-3.5 w-3.5" />
              User Learning Analysis
              {learningAnalysisLoading && viewMode === 'learning' && (
                <Loader2 className="h-3 w-3 animate-spin ml-0.5" />
              )}
            </button>
          </div>
          {viewMode === 'learning' && hasLearning && learningAnalysis && (
            <button
              type="button"
              onClick={handleRefreshLearningAnalysis}
              disabled={learningAnalysisLoading}
              className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-500 hover:text-violet-600 disabled:opacity-50 transition"
              data-testid="analysis-view-learning-refresh"
              title="Regenerate the learning view from scratch"
            >
              {learningAnalysisLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
              Refresh
            </button>
          )}
          {viewMode === 'learning' && learningAnalysis && (
            <span className="text-[11px] text-violet-600 font-medium ml-auto">
              Rewritten through your learning · Transcript unchanged
            </span>
          )}
          {viewMode === 'learning' && !learningAnalysis && learningAnalysisLoading && (
            <span className="text-[11px] text-slate-500 ml-auto">Generating…</span>
          )}
        </div>

        {/* ===================== METRICS STRIP ===================== */}
        <div className="shrink-0 px-5 md:px-6 py-3 bg-[#f7f7f5]">
          <div className="flex items-stretch gap-3 flex-wrap">
            {metricTiles.length === 0 && (
              <button
                type="button"
                onClick={onEditMetrics}
                className="flex-1 bg-white rounded-2xl border border-dashed border-slate-300 px-4 py-3 text-left hover:border-violet-400 hover:bg-violet-50/40 transition"
                data-testid="metrics-empty-cta"
              >
                <p className="text-[13px] font-semibold text-slate-700">Add performance metrics</p>
                <p className="text-[11px] text-slate-500 mt-1">ROAS, CTR, Hold rate, CVR, CPA — click here to enter them</p>
              </button>
            )}
            {metricTiles.map((t, i) => (
              <StatCard key={i} value={t.value} label={t.label} delta={t.delta} deltaPositive={t.deltaPositive} wide={!!t.wide} />
            ))}
            {(insightHead || insightBody) && (
              <div className="flex-[1.6] bg-gradient-to-br from-violet-50 via-white to-white rounded-2xl border border-violet-100 px-4 py-3">
                <p className="text-[13px] font-semibold text-slate-900 leading-tight">{insightHead || 'Insight'}</p>
                <p className="text-[11px] text-slate-600 mt-1.5 line-clamp-2">{insightBody}</p>
              </div>
            )}
          </div>
        </div>

        {/* ===================== BODY (2 columns, single viewport) ===================== */}
        <div className="flex-1 min-h-0 px-5 md:px-6 pb-5 md:pb-6">
          <div className="h-full grid grid-cols-1 lg:grid-cols-[minmax(0,340px)_minmax(0,1fr)] gap-4">
            {/* ============ LEFT: MEDIA ============ */}
            <section className="min-h-0 flex flex-col rounded-2xl bg-slate-900 relative overflow-hidden">
              {/* Claim tag top-left */}
              <div className="absolute top-3 left-3 z-10">
                <span className="px-2.5 py-1 rounded-md bg-black/60 text-white/90 text-[10px] font-semibold tracking-wide uppercase backdrop-blur-sm">
                  Claim
                </span>
              </div>

              {/* Media */}
              <div className="flex-1 min-h-0 flex items-center justify-center relative">
                {videoUrl ? (
                  <video
                    ref={videoRef}
                    src={videoUrl}
                    className="w-full h-full object-contain"
                    onLoadedMetadata={onLoadedMeta}
                    onTimeUpdate={onTimeUpdate}
                    onPlay={() => setIsPlaying(true)}
                    onPause={() => setIsPlaying(false)}
                    controls
                    controlsList="nodownload"
                    disablePictureInPicture
                    playsInline
                    data-testid="media-video"
                  />
                ) : rawImageSrc ? (
                  <img
                    src={rawImageSrc}
                    alt=""
                    className="w-full h-full object-contain"
                    data-testid="media-image"
                  />
                ) : primaryFrameUrl ? (
                  <img
                    src={primaryFrameUrl}
                    alt=""
                    className="w-full h-full object-contain"
                    data-testid="media-frame"
                  />
                ) : (
                  <div className="text-slate-500 text-xs">No media available</div>
                )}

                {/* Central play button — visible for videos until the video
                    element has been mounted (lazy loads on first tap). */}
                {creative?.is_video && !videoUrl && (
                  <button
                    type="button"
                    onClick={togglePlay}
                    className="absolute inset-0 flex items-center justify-center group"
                    aria-label={loadingVideo ? 'Loading video' : 'Play video'}
                    data-testid="media-play-toggle"
                    disabled={loadingVideo}
                  >
                    <span className="w-14 h-14 rounded-full bg-white/90 flex items-center justify-center shadow-lg opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-all">
                      {loadingVideo
                        ? <Loader2 className="h-6 w-6 text-slate-800 animate-spin" />
                        : <Play className="h-6 w-6 text-slate-800 ml-0.5" />
                      }
                    </span>
                  </button>
                )}

                {/* Bottom-right time badge (video) */}
                {videoUrl && (
                  <span className="absolute bottom-3 right-3 px-2 py-0.5 rounded bg-black/60 text-white/90 text-[10.5px] font-mono tabular-nums pointer-events-none">
                    {fmtClock(currentTime)} / {fmtClock(duration || audio?.duration_seconds)}
                  </span>
                )}

                {/* Overlay caption of the "active" transcript line — a live
                    subtitle that auto-advances sentence-by-sentence as the
                    video plays (proportional to elapsed time within the
                    active segment) and swaps to English when the transcript
                    language is set to English. Fully hidden when captions
                    are toggled off. */}
                {videoUrl && captionsOn && activeIdx >= 0 && captionChunk.text && (
                  <div className="absolute top-14 left-3 right-3 pointer-events-none flex flex-col items-start gap-1.5" data-testid="video-caption">
                    {captionChunk.tag && (
                      <span
                        className={`px-2 py-0.5 rounded-full text-[10px] font-bold tracking-[0.14em] uppercase text-white backdrop-blur-sm ring-1 ring-white/25 ${
                          captionChunk.tag === 'hook' ? 'bg-emerald-500/80' :
                          captionChunk.tag === 'claim' ? 'bg-amber-500/85' :
                          captionChunk.tag === 'cta' ? 'bg-violet-500/85' : 'bg-white/25'
                        }`}
                        data-testid="video-caption-tag"
                      >
                        {captionChunk.tag === 'hook' ? 'Hook' : captionChunk.tag === 'claim' ? 'Claim' : 'Call to action'}
                      </span>
                    )}
                    <p
                      key={`${activeIdx}-${captionChunk.chunkIdx}`}
                      className="text-white text-[13.5px] font-medium leading-snug drop-shadow-md bg-black/45 px-2.5 py-1.5 rounded-md backdrop-blur-sm max-w-full animate-caption-in"
                      data-testid="video-caption-text"
                    >
                      {captionChunk.text}
                    </p>
                  </div>
                )}

                {/* Caption visibility toggle — top-right of the media card,
                    below any built-in dialog close button; independent of
                    the native <video controls> so it stays reachable while
                    the video is playing. Persists across sessions via
                    localStorage. */}
                {videoUrl && (
                  <button
                    type="button"
                    onClick={() => setCaptionsOn((v) => !v)}
                    className="absolute top-3 right-3 z-10 h-8 px-2.5 rounded-full bg-black/55 hover:bg-black/70 text-white text-[10px] font-bold tracking-[0.14em] uppercase backdrop-blur-sm flex items-center gap-1.5 ring-1 ring-white/15 transition-colors"
                    aria-pressed={captionsOn}
                    aria-label={captionsOn ? 'Hide captions' : 'Show captions'}
                    title={captionsOn ? 'Hide captions' : 'Show captions'}
                    data-testid="caption-toggle"
                  >
                    {captionsOn
                      ? <><Eye className="h-3.5 w-3.5" /> CC on</>
                      : <><EyeOff className="h-3.5 w-3.5" /> CC off</>
                    }
                  </button>
                )}
              </div>

              {/* Timeline scrubber + legend */}
              {timelineSegments.length > 0 && (
                <div className="shrink-0 px-3 py-3 bg-slate-900/95 border-t border-white/5">
                  <div className="relative h-2 rounded-full bg-white/10 overflow-hidden">
                    {timelineSegments.map((t, i) => (
                      <span
                        key={i}
                        className={`absolute top-0 bottom-0 ${
                          t.tag === 'hook' ? 'bg-emerald-400' :
                          t.tag === 'claim' ? 'bg-amber-400' :
                          t.tag === 'cta' ? 'bg-violet-400' : 'bg-white/20'
                        }`}
                        style={{ left: t.left, width: t.width }}
                      />
                    ))}
                    {/* Playhead */}
                    {(duration || audio?.duration_seconds) > 0 && (
                      <span
                        className="absolute top-1/2 -translate-y-1/2 w-2.5 h-2.5 rounded-full bg-white ring-2 ring-slate-900"
                        style={{ left: `calc(${(currentTime / (duration || audio?.duration_seconds || 1)) * 100}% - 5px)` }}
                      />
                    )}
                  </div>
                  <div className="mt-2 flex items-center gap-3 text-[10px] text-white/70">
                    <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-400" /> Hook</span>
                    <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-400" /> Claims</span>
                    <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-violet-400" /> Call to action</span>
                  </div>
                </div>
              )}
            </section>

            {/* ============ RIGHT: STRUCTURED PANELS ============
                Cards flow naturally top-to-bottom. Transcript panel sizes
                to its content (capped so long transcripts don't push the
                mini cards / meta / learning off-screen); if the total column
                is taller than the viewport, the column scrolls. */}
            <section className="min-h-0 flex flex-col gap-3 overflow-y-auto pr-1">
              {/* Transcript — with Original / English toggle for regional-language ads.
                  Sizes to content; internal scroll only when a very long
                  transcript would otherwise push the cards below off-screen. */}
              {(segments.length > 0 || hasEnglishTranslation) && (
                <div className="bg-white rounded-2xl border border-slate-200 p-4 shrink-0">
                  <div className="flex items-center justify-between gap-2 mb-2.5">
                    <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-[0.14em]">
                      Transcript
                      {audio?.language && audio.language !== 'Unknown' && (
                        <span className="ml-2 px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 text-[9px] tracking-wider ring-1 ring-slate-200">
                          {String(audio.language).toUpperCase()}
                        </span>
                      )}
                    </p>
                    {hasEnglishTranslation && (
                      <div
                        className="inline-flex items-center gap-0.5 p-0.5 rounded-full bg-slate-100 text-[10.5px] font-semibold"
                        data-testid="transcript-lang-toggle"
                        role="tablist"
                      >
                        <button
                          type="button"
                          role="tab"
                          aria-selected={transcriptLang === 'original'}
                          onClick={() => setTranscriptLang('original')}
                          className={`px-2.5 py-1 rounded-full transition-colors ${transcriptLang === 'original' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                          data-testid="transcript-lang-original"
                        >
                          Original
                        </button>
                        <button
                          type="button"
                          role="tab"
                          aria-selected={transcriptLang === 'english'}
                          onClick={() => setTranscriptLang('english')}
                          className={`px-2.5 py-1 rounded-full transition-colors ${transcriptLang === 'english' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                          data-testid="transcript-lang-english"
                        >
                          English
                        </button>
                      </div>
                    )}
                  </div>
                  {transcriptLang === 'english' && hasEnglishTranslation ? (
                    segments.length > 0 ? (
                      <ul className="space-y-1 max-h-[38vh] overflow-y-auto pr-1" data-testid="structured-transcript-english">
                        {segments.map((s, i) => (
                          <li key={i}>
                            <button
                              type="button"
                              onClick={() => seekTo(s.start)}
                              className={`w-full text-left flex items-start gap-3 px-2.5 py-1.5 rounded-lg transition-colors ${
                                i === activeIdx ? 'bg-emerald-50 ring-1 ring-emerald-100' : 'hover:bg-slate-50'
                              }`}
                              data-testid={`transcript-english-line-${i}`}
                            >
                              <span className="text-[11px] font-mono tabular-nums text-slate-400 pt-0.5 shrink-0 w-9">
                                {fmtClock(s.start)}
                              </span>
                              <span className="flex-1 text-[13px] text-slate-800 leading-snug">
                                {englishBySegment[i] || <span className="text-slate-400 italic">—</span>}
                              </span>
                              <TagPill tag={s.tag} />
                            </button>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p
                        className="p-3 rounded-lg bg-emerald-50/40 ring-1 ring-emerald-100 text-[13px] text-slate-800 leading-relaxed whitespace-pre-wrap max-h-[38vh] overflow-y-auto"
                        data-testid="structured-transcript-english"
                      >
                        {englishTranscript}
                      </p>
                    )
                  ) : segments.length > 0 ? (
                    <ul className="space-y-1 max-h-[38vh] overflow-y-auto pr-1" data-testid="structured-transcript">
                      {segments.map((s, i) => (
                        <li key={i}>
                          <button
                            type="button"
                            onClick={() => seekTo(s.start)}
                            className={`w-full text-left flex items-start gap-3 px-2.5 py-1.5 rounded-lg transition-colors ${
                              i === activeIdx ? 'bg-violet-50 ring-1 ring-violet-100' : 'hover:bg-slate-50'
                            }`}
                            data-testid={`transcript-line-${i}`}
                          >
                            <span className="text-[11px] font-mono tabular-nums text-slate-400 pt-0.5 shrink-0 w-9">
                              {fmtClock(s.start)}
                            </span>
                            <span className="flex-1 text-[13px] text-slate-800 leading-snug">{s.text}</span>
                            <TagPill tag={s.tag} />
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-[12px] text-slate-400 italic">No transcript captured.</p>
                  )}
                </div>
              )}

              {/* Four mini cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <MiniInsightCard
                  label="Hook"
                  quote={effectiveAudioAnalysis?.hook || effectiveAnalysis?.headline}
                  comparison={effectiveAudioAnalysis?.hook_strength ? `${effectiveAudioAnalysis.hook_strength} hook` : null}
                  comparisonPositive={effectiveAudioAnalysis?.hook_strength === 'strong'}
                />
                <MiniInsightCard
                  label="Call to action"
                  quote={(Array.isArray(effectiveAudioAnalysis?.ctas) && effectiveAudioAnalysis.ctas[0]) || effectiveAnalysis?.cta}
                  comparison={effectiveAnalysis?.cta && effectiveAnalysis.cta !== 'Not visible' ? 'On-screen + spoken' : null}
                  comparisonPositive
                />
                <MiniInsightCard
                  label="Audio"
                  quote={[effectiveAudioLanguage, effectiveAudioAnalysis?.tone, effectiveAudioAnalysis?.pacing && `${effectiveAudioAnalysis.pacing} pace`].filter(Boolean).join(' · ') || 'No audio detected'}
                  comparison={effectiveAudioAnalysis?.pacing || null}
                />
                <MiniInsightCard
                  label="Visual"
                  quote={[effectiveAnalysis?.style, effectiveAnalysis?.background, effectiveAnalysis?.dominant_colors && effectiveAnalysis.dominant_colors.slice(0, 2).join('/')].filter(Boolean).join(' · ')}
                  comparison={effectiveAnalysis?.ad_format || null}
                />
              </div>

              {/* Key pointers — 3-block panel, Learning view only. */}
              {showLearningView && (() => {
                const kp = learningAnalysis?.key_pointers || {};
                const hasAny = !!(kp.what_worked || kp.why_it_worked || kp.what_to_replicate);
                if (!hasAny) return null;
                const rows = [
                  { label: 'What worked', text: kp.what_worked, tone: 'emerald' },
                  { label: 'Why it worked', text: kp.why_it_worked, tone: 'violet' },
                  { label: 'What to replicate', text: kp.what_to_replicate, tone: 'amber' },
                ];
                const toneMap = {
                  emerald: 'text-emerald-700 bg-emerald-50 ring-emerald-100',
                  violet: 'text-violet-700 bg-violet-50 ring-violet-100',
                  amber: 'text-amber-700 bg-amber-50 ring-amber-100',
                };
                return (
                  <div className="bg-white rounded-2xl border border-violet-100 px-4 py-4" data-testid="key-pointers-panel">
                    <div className="flex items-center gap-2 mb-3">
                      <Lightbulb className="h-3.5 w-3.5 text-violet-500" />
                      <p className="text-[11px] font-semibold text-slate-600 uppercase tracking-[0.14em]">Key pointers</p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      {rows.map((r) => (
                        <div key={r.label} className="flex flex-col gap-1.5">
                          <span className={`inline-flex self-start text-[10px] font-semibold uppercase tracking-[0.12em] px-2 py-0.5 rounded-full ring-1 ${toneMap[r.tone]}`}>
                            {r.label}
                          </span>
                          <p className="text-[13px] text-slate-800 leading-snug whitespace-normal break-words">
                            {r.text || <span className="text-slate-400 italic">—</span>}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {/* Meta row — symmetric two-row layout.
                  Row 1: HEADLINE {text}  ·  BUTTON {pill}
                  Row 2: Category tags (same left indent as row 1). */}
              <div className="bg-white rounded-2xl border border-slate-200 px-4 py-3">
                <div className="flex flex-wrap items-center gap-x-8 gap-y-1.5 text-[13px]">
                  {effectiveAnalysis?.headline && effectiveAnalysis.headline !== 'Not visible' && (
                    <div className="flex items-baseline gap-2 min-w-0">
                      <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-[0.14em] shrink-0">Headline</span>
                      <span className="text-slate-900 font-semibold truncate max-w-[360px]">{effectiveAnalysis.headline}</span>
                    </div>
                  )}
                  {effectiveAnalysis?.cta && effectiveAnalysis.cta !== 'Not visible' && effectiveAnalysis.cta !== 'None' && (
                    <div className="flex items-baseline gap-2">
                      <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-[0.14em] shrink-0">Button</span>
                      <span className="px-2.5 py-0.5 rounded-md bg-emerald-100 text-emerald-700 text-[12px] font-semibold">
                        {effectiveAnalysis.cta}
                      </span>
                    </div>
                  )}
                </div>
                {categoryTags.length > 0 && (
                  <div className="mt-2 flex flex-wrap items-center gap-1.5">
                    {categoryTags.map((tag, i) => (
                      <span key={i} className="px-2.5 py-0.5 rounded-md bg-slate-100 text-slate-700 text-[12px] font-medium">
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Learning capture — LEARNING pill lives here (moved out of
                  the header where it was cramped), acts as a saved-state
                  indicator; explicit Save button next to Rephrase. */}
              <div className="bg-white rounded-2xl border border-amber-200 p-4">
                <div className="flex items-center justify-between gap-2 mb-2">
                  <p className="text-[11px] font-bold text-amber-700 tracking-[0.14em] uppercase inline-flex items-center gap-1.5">
                    <Lightbulb className="h-3 w-3" /> Your learning
                    {learningSaved && (
                      <span
                        className="ml-1 inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-[0.14em] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 ring-1 ring-amber-200"
                        data-testid="learning-saved-badge"
                      >
                        Saved
                      </span>
                    )}
                    {learningDirty && (
                      <span
                        className="ml-1 inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-[0.14em] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600 ring-1 ring-slate-200"
                        data-testid="learning-unsaved-badge"
                      >
                        Unsaved
                      </span>
                    )}
                  </p>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={onRephraseLearning}
                      disabled={rephrasingLearning || !(creative?.learning || '').trim()}
                      className="inline-flex items-center gap-1 text-[10px] font-semibold text-violet-600 hover:text-violet-800 disabled:opacity-40 uppercase tracking-wider"
                      data-testid="footer-rephrase-btn"
                    >
                      {rephrasingLearning ? <Loader2 className="h-3 w-3 animate-spin" /> : <Wand2 className="h-3 w-3" />}
                      Rephrase
                    </button>
                    <button
                      type="button"
                      onClick={saveLearningNow}
                      disabled={!learningDirty}
                      className="inline-flex items-center gap-1.5 h-7 px-3 rounded-full bg-amber-500 hover:bg-amber-600 text-white text-[11px] font-bold uppercase tracking-[0.12em] shadow-sm disabled:bg-slate-200 disabled:text-slate-400 disabled:shadow-none transition-colors"
                      data-testid="learning-save-btn"
                    >
                      Save
                    </button>
                  </div>
                </div>
                <Textarea
                  value={learningDraft}
                  onChange={(e) => setLearningDraft(e.target.value)}
                  onKeyDown={(e) => {
                    // Cmd/Ctrl+Enter → save
                    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                      e.preventDefault();
                      saveLearningNow();
                    }
                  }}
                  onBlur={saveLearningNow}
                  placeholder="What did you learn from this creative? e.g. Bold headline + price tag drove 2x higher CTR"
                  className="min-h-[70px] text-[13px] bg-amber-50/40 border-amber-200 focus-visible:ring-amber-400 resize-none"
                  data-testid="learning-detail-textarea"
                />
              </div>
            </section>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default CreativeStructuredDetail;
